import { STATUS } from 'common/constants/storeConstants'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { aquaFetch } from 'common/utils/ActionUtils'

const INITIAL_STATE = {
    error: undefined,
    data: undefined,
    status: STATUS.IDLE,
}

const useFetch = (url, options, fetchOptions) => {
    const cache = useRef({})

    // Used to prevent state update if the component is unmounted
    const cancelRequest = useRef(false)
    // Contrôleur d'annulation de la requête en vol (libère la connexion navigateur au démontage/refetch).
    const abortRef = useRef(undefined)

    const {
        formatResult = r => r,
    } = fetchOptions ?? {}

    // Keep state logic separated
    const fetchReducer = (state, action) => {
        switch (action.type) {
            case 'loading': return { ...INITIAL_STATE, status: STATUS.LOADING, data: state.data }
            case 'fetched': return { ...INITIAL_STATE, status: STATUS.SUCCEEDED, data: action.payload }
            case 'error': return { ...INITIAL_STATE, status: STATUS.REJECTED, error: action.payload }
            case 'reset': return { ...INITIAL_STATE }
            default: return state
        }
    }

    const [state, dispatch] = useReducer(fetchReducer, INITIAL_STATE)

    // La clé de requête inclut le body/method (options), pas seulement l'url :
    // les endpoints POST portent leurs paramètres dans le body (ex. /GDL/customerStats
    // avec { group, action, user }). Sans ça, changer un paramètre ne redéclenchait
    // aucun appel (l'url étant constante) et les données restaient figées.
    const requestKey = url ? `${url}::${JSON.stringify(options ?? {})}` : undefined

    const fetchData = async () => {
        // Annule une éventuelle requête précédente encore en vol avant d'en lancer une nouvelle.
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller
        cancelRequest.current = false
        dispatch({ type: 'loading' })

        try {
            const result = await aquaFetch(url, { ...options, signal: controller.signal })
                .then(formatResult)
                .catch(e => {
                    throw new Error(e)
                })

            cache.current[requestKey] = result
            if (cancelRequest.current || controller.signal.aborted) {
                return
            }

            dispatch({ type: 'fetched', payload: result })
        } catch (error) {
            if (cancelRequest.current || controller.signal.aborted) {
                return
            }

            dispatch({ type: 'error', payload: error.message })
        }
    }

    useEffect(() => {
        // Do nothing if the url is not given
        if (!url) {
            return undefined
        }

        // Requête déjà en cache (même url + mêmes paramètres) : on ressert sans refetch
        if (cache.current[requestKey] !== undefined) {
            dispatch({ type: 'fetched', payload: cache.current[requestKey] })
            return undefined
        }

        fetchData()

        // Use the cleanup function for avoiding a possibly
        // state update after the component was unmounted, and abort the in-flight request
        // so it frees the browser connection instead of blocking later calls.
        return () => {
            cancelRequest.current = true
            abortRef.current?.abort()
        }
    }, [requestKey])

    const reset = useCallback(() => dispatch({ type: 'reset' }), [dispatch])

    return {
        ...state,
        reload: fetchData,
        reset,
    }
}

export default useFetch